// AdminScreens.jsx — Complete rewrite with all fixes applied
// Fixes: Admin disable bug, Edit user, Edit entity, Reverse entry confirmation
// New: Manual journal entry, TPA settlement, Salary journal, Statutory payment, GST payment

import { useState, useEffect } from "react";
import { api } from "../services/api";
import { AppDatePicker, AppSelect } from "../components/shared/FormControls";

// ─────────────────────────────────────────────────────────────────────────────
// COLUMN MAPPING (unchanged from developer version — keeping his improvements)
// ─────────────────────────────────────────────────────────────────────────────

const SYSTEM_FIELDS = [
  { key: "transaction_date", label: "Transaction date",     required: true,  hint: "The date of each bill" },
  { key: "department",       label: "Department / service", required: true,  hint: "What service was provided" },
  { key: "payment_mode",     label: "Payment mode",         required: true,  hint: "How the patient paid" },
  { key: "gross_amount",     label: "Gross amount",         required: true,  hint: "Total amount including GST" },
  { key: "gst_rate",         label: "GST rate",             required: false, hint: "Tax rate — optional if set per stream" },
  { key: "patient_type",     label: "Patient type",         required: false, hint: "OP / IP — optional" },
  { key: "bill_number",      label: "Bill number",          required: false, hint: "Reference number — optional" },
];

export default function ColumnMapping({ entity, onEntityUpdated }) {
  const [step,        setStep]        = useState(1);
  const [detected,    setDetected]    = useState(null);
  const [columnMap,   setColumnMap]   = useState({});
  const [deptMap,     setDeptMap]     = useState({});
  const [payMap,      setPayMap]      = useState({});
  const [profileName, setProfileName] = useState("");
  const [isDefault,   setIsDefault]   = useState(false);
  const [dateFormat,  setDateFormat]  = useState("DD/MM/YYYY");
  const [loading,     setLoading]     = useState(false);
  const [loadingLedgers, setLoadingLedgers] = useState(false);
  const [zohoLedgers, setZohoLedgers] = useState([]);
  const [saved,       setSaved]       = useState(false);
  const [error,       setError]       = useState("");
  const [ledgerError, setLedgerError] = useState("");
  const savedProfiles = entity?.columnMappingProfiles || [];
  const sortedZohoLedgers = [...zohoLedgers]
    .filter(account => account?.account_name)
    .sort((a, b) => a.account_name.localeCompare(b.account_name));

  useEffect(() => {
    if (entity?.accountingSoftware?.type === "zoho_api") loadZohoLedgers();
  }, [entity?._id, entity?.accountingSoftware?.type]);

  async function loadZohoLedgers() {
    if (!entity?._id) return;
    setLoadingLedgers(true); setLedgerError("");
    try {
      const res = await api.get(`/entities/${entity._id}/zoho/accounts`);
      setZohoLedgers(res.accounts || []);
    } catch (err) { setLedgerError(err.message); }
    finally { setLoadingLedgers(false); }
  }

  async function uploadSample(e) {
    const file = e.target.files[0];
    if (!file) return;
    setLoading(true); setError("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.upload(`/entities/${entity._id}/mapping/detect`, fd);
      setDetected(res);
      if (res.suggestions) setColumnMap(res.suggestions);
      setStep(2);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  function getUniqueValues(columnName) {
    if (!detected?.samples || !columnName) return [];
    const seen = new Set();
    return (detected.samples[columnName] || []).filter(value => {
      const key = String(value || "").trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key); return true;
    });
  }

  function getZohoLedgerOptions(selectedValue) {
    const selectedIsMissing = selectedValue && !sortedZohoLedgers.some(
      account => account.account_name === selectedValue
    );

    const options = sortedZohoLedgers.map(account => ({
      value: account.account_name,
      label: `${account.account_name}${account.account_type ? ` — ${account.account_type}` : ""}`,
    }));

    return selectedIsMissing
      ? [{ value: selectedValue, label: `${selectedValue} (saved mapping)` }, ...options]
      : options;
  }

  async function saveProfile() {
    if (!profileName.trim()) { setError("Give this profile a name."); return; }
    const missing = SYSTEM_FIELDS.filter(f => f.required && !columnMap[f.key]);
    if (missing.length > 0) { setError(`Map these required fields first: ${missing.map(f => f.label).join(", ")}`); return; }
    setLoading(true); setError("");
    try {
      await api.post(`/entities/${entity._id}/mapping/profiles`, { profileName: profileName.trim(), isDefault, columnMap, departmentValueMap: deptMap, paymentModeValueMap: payMap, dateFormat });
      const updated = await api.get(`/entities/${entity._id}`);
      if (onEntityUpdated) onEntityUpdated(updated.entity);
      setSaved(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function deleteProfile(profile) {
    if (!profile?._id) return;
    if (!window.confirm(`Delete mapping profile "${profile.profileName}"?`)) return;
    setLoading(true); setError("");
    try {
      const res = await api.delete(`/entities/${entity._id}/mapping/profiles/${profile._id}`);
      if (onEntityUpdated) onEntityUpdated(res.entity);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  if (!entity) return <div style={S.page}><div style={S.emptyBox}>No entity is available yet. Create or load an entity before using this screen.</div></div>;

  if (saved) {
    return (
      <div style={S.page}>
        <div style={{ background: "#E8F5EE", borderRadius: 14, padding: 36, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
          <h2 style={{ color: "#1A7A4A", margin: "0 0 8px" }}>Mapping profile saved</h2>
          <p style={{ color: "#607D8B", margin: "0 0 20px" }}>"{profileName}" is ready. Now upload the billing file to generate journal entries.</p>
          <button onClick={() => { setSaved(false); setStep(1); setDetected(null); setColumnMap({}); setDeptMap({}); setPayMap({}); setProfileName(""); }} style={S.btn}>Set up another profile</button>
        </div>
        <SavedMappingProfiles profiles={savedProfiles} onDelete={deleteProfile} />
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Billing software setup</h1>
      <p style={S.subtitle}>Map your billing export to the system. Done once — works for all future uploads.</p>
      <SavedMappingProfiles profiles={savedProfiles} onDelete={deleteProfile} />

      <div style={{ display: "flex", gap: 0, marginBottom: 28 }}>
        {["Upload sample", "Map columns", "Map values", "Save"].map((label, i) => (
          <div key={i} style={{ flex: 1, textAlign: "center" }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", margin: "0 auto 6px", background: step > i+1 ? "#1A7A4A" : step === i+1 ? "#1B3F6E" : "#ECEFF1", color: step >= i+1 ? "#fff" : "#90A4AE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>
              {step > i+1 ? "✓" : i+1}
            </div>
            <div style={{ fontSize: 11, color: step === i+1 ? "#1B3F6E" : "#90A4AE", fontWeight: step === i+1 ? 600 : 400 }}>{label}</div>
          </div>
        ))}
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {step === 1 && (
        <div style={S.card}>
          <div style={S.sectionLabel}>Upload a sample billing file</div>
          <p style={{ fontSize: 13, color: "#607D8B", marginBottom: 16 }}>Upload one month of your custom Excel. System detects column names automatically.</p>
          <label style={{ display: "block", border: "2px dashed #CFD8DC", borderRadius: 10, padding: "32px 24px", textAlign: "center", cursor: "pointer" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1B3F6E" }}>{loading ? "Detecting columns…" : "Click to upload sample file"}</div>
            <div style={{ fontSize: 12, color: "#90A4AE", marginTop: 4 }}>Accepts .xlsx, .xls, .csv</div>
            <input type="file" accept=".xlsx,.xls,.csv" onChange={uploadSample} style={{ display: "none" }} disabled={loading} />
          </label>
        </div>
      )}

      {step === 2 && detected && (
        <div style={S.card}>
          <div style={S.sectionLabel}>Map columns — {detected.headers?.length} columns found, {detected.totalRows} rows</div>
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Date format in your file</label>
            <select value={dateFormat} onChange={e => setDateFormat(e.target.value)} style={S.select}>
              <option value="DD/MM/YYYY">DD/MM/YYYY (e.g. 15/04/2025)</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD (e.g. 2025-04-15)</option>
              <option value="DD-MM-YYYY">DD-MM-YYYY (e.g. 15-04-2025)</option>
              <option value="MM/DD/YYYY">MM/DD/YYYY (e.g. 04/15/2025)</option>
            </select>
          </div>
          {SYSTEM_FIELDS.map(field => (
            <div key={field.key} style={{ marginBottom: 16 }}>
              <label style={{ ...S.label, color: field.required ? "#1B3F6E" : "#607D8B" }}>
                {field.label} {field.required ? <span style={{ color: "#B71C1C" }}>*</span> : "(optional)"}
              </label>
              <select value={columnMap[field.key] || ""} onChange={e => setColumnMap(m => ({ ...m, [field.key]: e.target.value }))} style={S.select}>
                <option value="">— Not in this file —</option>
                {detected.headers?.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
              {columnMap[field.key] && detected.samples?.[columnMap[field.key]]?.length > 0 && (
                <div style={{ fontSize: 11, color: "#90A4AE", marginTop: 4 }}>Sample: {detected.samples[columnMap[field.key]].join(", ")}</div>
              )}
              <div style={{ fontSize: 11, color: "#90A4AE", marginTop: 2 }}>{field.hint}</div>
            </div>
          ))}
          <button onClick={() => setStep(3)} style={S.btn}>Next — map values →</button>
        </div>
      )}

      {step === 3 && (
        <div style={S.card}>
          <div style={S.sectionLabel}>Map department and payment mode values</div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", marginBottom: 16, background: "#F5F8FB", border: "1px solid #E0E7EF", borderRadius: 8 }}>
            <div style={{ fontSize: 12, color: "#607D8B" }}>
              {loadingLedgers ? "Loading ledgers from Zoho Books…" : `${sortedZohoLedgers.length} Zoho ledgers available`}
            </div>
            <button type="button" onClick={loadZohoLedgers} disabled={loadingLedgers || entity?.accountingSoftware?.type !== "zoho_api"} style={{ ...S.outlineBtn, padding: "7px 11px" }}>
              {loadingLedgers ? "Loading…" : "Refresh ledgers"}
            </button>
          </div>
          {ledgerError && <div style={S.errorBox}>{ledgerError}</div>}
          {entity?.accountingSoftware?.type !== "zoho_api" && (
            <div style={S.errorBox}>Connect this entity to Zoho Books before mapping billing values to ledgers.</div>
          )}
          {columnMap.department && (
            <>
              <div style={{ ...S.label, marginBottom: 10 }}>Department values — what your file shows → what system calls it</div>
              {(getUniqueValues(columnMap.department).length === 0
                ? ["OP/IP", "PHARMACY", "LAB", "XRAY", "ECG", "SCAN", "PHYSIO", "DIALYSIS"]
                : getUniqueValues(columnMap.department)
              ).map(val => (
                <div key={val} style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
                  <div style={{ width: 130, padding: "8px 12px", background: "#f0f4f8", borderRadius: 6, fontSize: 13, fontFamily: "monospace", color: "#1B3F6E" }}>{val}</div>
                  <span style={{ color: "#90A4AE" }}>→</span>
                  <AppSelect
                    value={deptMap[val] || ""}
                    onChange={value => setDeptMap(m => ({ ...m, [val]: value }))}
                    options={getZohoLedgerOptions(deptMap[val])}
                    placeholder="Select Zoho ledger"
                    isClearable
                    isDisabled={loadingLedgers || sortedZohoLedgers.length === 0}
                    style={{ flex: 1 }}
                  />
                </div>
              ))}
            </>
          )}
          {columnMap.payment_mode && (
            <>
              <div style={{ ...S.label, marginTop: 20, marginBottom: 10 }}>Payment mode values</div>
              {(getUniqueValues(columnMap.payment_mode).length === 0
                ? ["CASH", "UPI", "CARD", "COMPANY", "INSURANCE", "PATIENT DUE"]
                : getUniqueValues(columnMap.payment_mode)
              ).map(val => (
                <div key={val} style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
                  <div style={{ width: 130, padding: "8px 12px", background: "#f0f4f8", borderRadius: 6, fontSize: 13, fontFamily: "monospace", color: "#1B3F6E" }}>{val}</div>
                  <span style={{ color: "#90A4AE" }}>→</span>
                  <AppSelect
                    value={payMap[val] || ""}
                    onChange={value => setPayMap(m => ({ ...m, [val]: value }))}
                    options={getZohoLedgerOptions(payMap[val])}
                    placeholder="Select Zoho ledger"
                    isClearable
                    isDisabled={loadingLedgers || sortedZohoLedgers.length === 0}
                    style={{ flex: 1 }}
                  />
                </div>
              ))}
            </>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button onClick={() => setStep(2)} style={S.outlineBtn}>← Back</button>
            <button onClick={() => setStep(4)} style={S.btn}>Next — save profile →</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div style={S.card}>
          <div style={S.sectionLabel}>Save mapping profile</div>
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Profile name *</label>
            <input type="text" value={profileName} onChange={e => setProfileName(e.target.value)} placeholder="e.g. Daily Collection Excel, MOCDOC Default" style={{ ...S.input, width: "100%" }} />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, cursor: "pointer" }}>
            <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
            <span style={{ fontSize: 13, color: "#455A64" }}>Use as default profile (pre-selected on every upload)</span>
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setStep(3)} style={S.outlineBtn}>← Back</button>
            <button onClick={saveProfile} disabled={loading} style={{ ...S.btn, flex: 1, background: loading ? "#90A4AE" : "#1B3F6E" }}>
              {loading ? "Saving…" : "Save mapping profile"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SavedMappingProfiles({ profiles, onDelete }) {
  if (!profiles?.length) return null;
  return (
    <div style={{ ...S.card, marginBottom: 20 }}>
      <div style={S.sectionLabel}>Saved profiles</div>
      {profiles.map(p => (
        <div key={p._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f0f4f8" }}>
          <div>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#1B3F6E" }}>{p.profileName}</span>
            {p.isDefault && <span style={{ marginLeft: 8, fontSize: 10, background: "#EBF5FB", color: "#2E86AB", padding: "2px 8px", borderRadius: 10 }}>Default</span>}
          </div>
          <button onClick={() => onDelete(p)} style={{ background: "none", border: "1px solid #FFCDD2", color: "#B71C1C", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>Delete</button>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ZOHO SETUP (keeping developer's version intact)
// ─────────────────────────────────────────────────────────────────────────────
export function ZohoSetup({ entity, onEntityUpdated }) {
  const [status,   setStatus]   = useState(null);
  const [form,     setForm]     = useState({ zohoOrgId: "", zohoClientId: "", zohoClientSecret: "" });
  const [accounts, setAccounts] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [checking, setChecking] = useState(false);
  const [error,    setError]    = useState("");
  const [message,  setMessage]  = useState("");

  useEffect(() => { if (entity?._id) loadStatus(); }, [entity?._id]);

  async function loadStatus() {
    if (!entity?._id) return;
    setChecking(true);
    try {
      const res = await api.get(`/entities/${entity._id}/zoho/status`);
      setStatus(res);
      setForm(f => ({ ...f, zohoOrgId: res.zohoOrgId || "", zohoClientId: "", zohoClientSecret: "" }));
    } catch (err) { setError(err.message); }
    finally { setChecking(false); }
  }

  async function saveConfig() {
    setLoading(true); setError(""); setMessage("");
    try {
      const res = await api.put(`/entities/${entity._id}/zoho/config`, form);
      if (onEntityUpdated) onEntityUpdated(res.entity);
      setMessage("Credentials saved. Now click Connect with Zoho.");
      await loadStatus();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function connectZoho() {
    setLoading(true); setError("");
    try {
      const res = await api.get(`/entities/${entity._id}/zoho/auth-url`);
      window.open(res.authUrl, "_blank", "width=600,height=700");
      setMessage("Complete the Zoho login in the popup. Then click Refresh status.");
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function testAccounts() {
    setLoading(true); setError("");
    try {
      const res = await api.get(`/entities/${entity._id}/zoho/accounts`);
      setAccounts(res.accounts || []);
      setMessage(`Connected. ${res.accounts?.length || 0} ledgers loaded from Zoho Books.`);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function disconnect() {
    if (!window.confirm("Disconnect Zoho? You will need to reconnect to push journal entries.")) return;
    setLoading(true); setError("");
    try {
      const res = await api.post(`/entities/${entity._id}/zoho/disconnect`);
      if (onEntityUpdated) onEntityUpdated(res.entity);
      setMessage("Zoho disconnected.");
      await loadStatus();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  if (!entity) return <div style={S.page}><div style={S.emptyBox}>No entity is available yet.</div></div>;

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Zoho Books Setup</h1>
      <p style={S.subtitle}>Connect {entity?.name} to Zoho Books for direct journal entry push.</p>

      {status && (
        <div style={{ ...S.card, background: status.connected ? "#E8F5EE" : "#FFF8E1", marginBottom: 16 }}>
          <div style={{ fontWeight: 600, color: status.connected ? "#1A7A4A" : "#C85A00", fontSize: 14 }}>
            {status.connected ? "✓ Connected to Zoho Books" : "⚠ Not connected"}
          </div>
          {status.connected && <div style={{ fontSize: 12, color: "#607D8B", marginTop: 4 }}>Organisation ID: {status.zohoOrgId}</div>}
        </div>
      )}

      <div style={S.card}>
        <div style={S.sectionLabel}>Zoho API credentials</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
          <FormField label="Zoho Organization ID *" value={form.zohoOrgId} onChange={v => setForm(f => ({ ...f, zohoOrgId: v }))} placeholder="e.g. 60012345678" />
          <FormField label="Zoho Client ID *" value={form.zohoClientId} onChange={v => setForm(f => ({ ...f, zohoClientId: v }))} placeholder="Client ID from Zoho API Console" />
          <div style={{ gridColumn: "1 / -1" }}>
            <FormField label="Zoho Client Secret *" type="password" value={form.zohoClientSecret} onChange={v => setForm(f => ({ ...f, zohoClientSecret: v }))} placeholder="Client Secret from Zoho API Console" />
          </div>
        </div>

        {error   && <div style={S.errorBox}>{error}</div>}
        {message && <div style={{ background: "#E8F5EE", border: "1px solid #C8E6C9", borderRadius: 8, padding: "12px 16px", color: "#1A7A4A", fontSize: 13, marginBottom: 12 }}>{message}</div>}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={saveConfig} disabled={loading} style={{ ...S.btn, width: "auto", padding: "11px 18px", background: loading ? "#90A4AE" : "#1B3F6E" }}>Save credentials</button>
          <button onClick={connectZoho} disabled={loading} style={{ ...S.btn, width: "auto", padding: "11px 18px", background: "#1A7A4A" }}>Connect with Zoho</button>
          <button onClick={loadStatus} disabled={loading || checking} style={S.outlineBtn}>Refresh status</button>
          <button onClick={testAccounts} disabled={loading || !status?.connected} style={S.outlineBtn}>Test accounts</button>
          {status?.connected && <button onClick={disconnect} disabled={loading} style={{ ...S.outlineBtn, borderColor: "#B71C1C", color: "#B71C1C" }}>Disconnect</button>}
        </div>
      </div>

      {accounts.length > 0 && (
        <div style={S.card}>
          <div style={{ fontWeight: 700, color: "#1B3F6E", marginBottom: 12 }}>Zoho accounts preview</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8 }}>
            {accounts.slice(0, 12).map(account => (
              <div key={account.account_id || account.account_name} style={{ border: "1px solid #E0E7EF", borderRadius: 8, padding: 10, background: "#FAFBFC" }}>
                <div style={{ fontSize: 13, color: "#1B3F6E", fontWeight: 700 }}>{account.account_name}</div>
                <div style={{ fontSize: 11, color: "#90A4AE", marginTop: 2 }}>{account.account_type}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE LEDGER (keeping developer's version)
// ─────────────────────────────────────────────────────────────────────────────
export function LedgerCreate({ entity }) {
  const [name,    setName]    = useState("");
  const [type,    setType]    = useState("expense");
  const [loading, setLoading] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountSearch, setAccountSearch] = useState("");
  const [result,  setResult]  = useState(null);
  const [error,   setError]   = useState("");
  const [accountError, setAccountError] = useState("");

  const typeOptions = [
    { value: "asset", label: "Asset" }, { value: "liability", label: "Liability" },
    { value: "income", label: "Income / Revenue" }, { value: "expense", label: "Expense" },
    { value: "equity", label: "Equity" },
  ];

  const filteredAccounts = accounts.filter(a => {
    const q = accountSearch.trim().toLowerCase();
    if (!q) return true;
    return [a.account_name, a.account_type, a.account_code].filter(Boolean).some(v => String(v).toLowerCase().includes(q));
  });

  useEffect(() => { if (entity?.accountingSoftware?.type === "zoho_api") loadAccounts(); }, [entity?._id, entity?.accountingSoftware?.type]);

  async function loadAccounts() {
    if (!entity?._id) return;
    setLoadingAccounts(true); setAccountError("");
    try {
      const res = await api.get(`/entities/${entity._id}/zoho/accounts`);
      setAccounts(res.accounts || []);
    } catch (err) { setAccountError(err.message); }
    finally { setLoadingAccounts(false); }
  }

  async function create() {
    if (!name.trim()) { setError("Enter a ledger name."); return; }
    setLoading(true); setError(""); setResult(null);
    try {
      const res = await api.post(`/entities/${entity._id}/zoho/create-ledger`, { accountName: name.trim(), accountType: type });
      setResult(res.account); setName("");
      await loadAccounts();
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  if (!entity) return <div style={S.page}><div style={S.emptyBox}>No entity is available yet.</div></div>;
  if (entity?.accountingSoftware?.type !== "zoho_api") {
    return <div style={S.page}><div style={{ background: "#FFF3E0", borderRadius: 12, padding: 24, color: "#7B4F00" }}><strong>Ledger creation requires Zoho API connection.</strong><br />Go to Zoho Setup to connect first.</div></div>;
  }

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Create ledger in Zoho Books</h1>
      <p style={S.subtitle}>Creates the account directly in {entity?.name}'s Zoho Books organisation.</p>
      <div style={S.card}>
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Ledger name *</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. CGST Payable, Doctor Incentive Expense" style={{ ...S.input, width: "100%" }} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={S.label}>Account type *</label>
          <AppSelect value={type} onChange={setType} options={typeOptions} />
        </div>
        {error  && <div style={S.errorBox}>{error}</div>}
        {result && <div style={{ background: "#E8F5EE", borderRadius: 8, padding: "12px 16px", marginBottom: 12, fontSize: 13, color: "#1A7A4A" }}>✓ Created: <strong>{result.account_name}</strong></div>}
        <button onClick={create} disabled={loading} style={{ ...S.btn, background: loading ? "#90A4AE" : "#1B3F6E" }}>{loading ? "Creating in Zoho…" : "Create ledger"}</button>
      </div>

      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 700, color: "#1B3F6E" }}>Zoho Books ledger list</div>
            <div style={{ fontSize: 12, color: "#607D8B", marginTop: 3 }}>{loadingAccounts ? "Loading…" : `${accounts.length} ledgers`}</div>
          </div>
          <button onClick={loadAccounts} disabled={loadingAccounts} style={{ ...S.outlineBtn, padding: "9px 14px" }}>{loadingAccounts ? "Refreshing…" : "Refresh from Zoho"}</button>
        </div>
        <input type="text" value={accountSearch} onChange={e => setAccountSearch(e.target.value)} placeholder="Search ledger name or type" style={{ ...S.input, width: "100%", marginBottom: 12 }} />
        {accountError && <div style={S.errorBox}>{accountError}</div>}
        {filteredAccounts.length > 0 && (
          <div style={{ border: "1px solid #E0E7EF", borderRadius: 10, overflow: "hidden", maxHeight: 400, overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Ledger name","Type","Code","Zoho ID"].map(h => <th key={h} style={{ position: "sticky", top: 0, padding: "10px 12px", background: "#1B3F6E", color: "#fff", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", textAlign: "left" }}>{h}</th>)}</tr></thead>
              <tbody>
                {filteredAccounts.map(a => (
                  <tr key={a.account_id} style={{ borderBottom: "1px solid #EEF2F5" }}>
                    <td style={{ padding: "10px 12px", fontSize: 13, color: "#1B3F6E", fontWeight: 600 }}>{a.account_name}</td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: "#455A64" }}>{a.account_type || "-"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: "#607D8B" }}>{a.account_code || "-"}</td>
                    <td style={{ padding: "10px 12px", fontSize: 11, color: "#90A4AE", fontFamily: "monospace" }}>{a.account_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FIXED ASSETS — with edit capability
// ─────────────────────────────────────────────────────────────────────────────
export function FixedAssets({ entity, user }) {
  const [assets,   setAssets]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showAdd,  setShowAdd]  = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState({ assetName: "", assetCategory: "medical_equipment", purchaseDate: "", vendorName: "", purchaseCost: "", usefulLifeYears: "", incomeTaxRate: "", incomeTaxBlock: "" });
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState("");

  const isAdmin  = user?.role === "iicipl_admin";
  const isPvtLtd = entity?.entityType === "pvt_ltd";

  const categories = [
    { value: "medical_equipment",    label: "Medical Equipment" },
    { value: "furniture_fixtures",   label: "Furniture & Fixtures" },
    { value: "building_improvement", label: "Building Improvement" },
    { value: "vehicle",              label: "Vehicle" },
    { value: "computer_peripherals", label: "Computer & Peripherals" },
    { value: "other",                label: "Other" },
  ];

  const caRates = { medical_equipment: 0.0667, furniture_fixtures: 0.0909, building_improvement: 0.0333, vehicle: 0.1111, computer_peripherals: 0.3333, other: 0.0909 };

  useEffect(() => {
    if (!entity?._id) return;
    api.get(`/entities/${entity._id}/fixed-assets`).then(res => setAssets(res.assets || [])).catch(console.error).finally(() => setLoading(false));
  }, [entity?._id]);

  function openEdit(asset) {
    setEditing(asset._id);
    setForm({ assetName: asset.assetName, assetCategory: asset.assetCategory, purchaseDate: asset.purchaseDate?.split("T")[0] || "", vendorName: asset.vendorName || "", purchaseCost: asset.purchaseCost || "", usefulLifeYears: asset.usefulLifeYears || "", incomeTaxRate: asset.incomeTaxRate ? (asset.incomeTaxRate * 100).toFixed(0) : "", incomeTaxBlock: asset.incomeTaxBlock || "" });
    setShowAdd(true);
  }

  function cancelForm() { setShowAdd(false); setEditing(null); setForm({ assetName: "", assetCategory: "medical_equipment", purchaseDate: "", vendorName: "", purchaseCost: "", usefulLifeYears: "", incomeTaxRate: "", incomeTaxBlock: "" }); setError(""); }

  async function saveAsset() {
    if (!form.assetName || !form.purchaseCost || !form.purchaseDate) { setError("Asset name, cost, and purchase date are required."); return; }
    setSaving(true); setError("");
    const payload = { ...form, purchaseCost: parseFloat(form.purchaseCost), usefulLifeYears: parseFloat(form.usefulLifeYears || 10), incomeTaxRate: parseFloat(form.incomeTaxRate || 15) / 100, companiesActRate: isPvtLtd ? caRates[form.assetCategory] : undefined, companiesActCurrentValue: parseFloat(form.purchaseCost), incomeTaxWDV: parseFloat(form.purchaseCost) };
    try {
      if (editing) {
        const res = await api.patch(`/entities/${entity._id}/fixed-assets/${editing}`, payload);
        setAssets(a => a.map(x => x._id === editing ? res.asset : x));
      } else {
        const res = await api.post(`/entities/${entity._id}/fixed-assets`, payload);
        setAssets(a => [res.asset, ...a]);
      }
      cancelForm();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  if (!entity) return <div style={S.page}><div style={S.emptyBox}>No entity is available yet.</div></div>;

  return (
    <div style={S.page}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1 style={S.h1}>Fixed asset register</h1>
        {isAdmin && !showAdd && <button onClick={() => setShowAdd(true)} style={{ ...S.btn, width: "auto", padding: "10px 20px" }}>+ Add asset</button>}
      </div>

      {showAdd && (
        <div style={S.card}>
          <div style={{ fontWeight: 600, color: "#1B3F6E", marginBottom: 16 }}>{editing ? "Edit asset" : "Add fixed asset"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <FormField label="Asset name *" value={form.assetName} onChange={v => setForm(f => ({ ...f, assetName: v }))} placeholder="e.g. Fuji Film X-Ray Machine" />
            <FormField label="Vendor name" value={form.vendorName} onChange={v => setForm(f => ({ ...f, vendorName: v }))} />
            <FormField label="Purchase date *" type="date" value={form.purchaseDate} onChange={v => setForm(f => ({ ...f, purchaseDate: v }))} />
            <FormField label="Purchase cost (₹) *" type="number" value={form.purchaseCost} onChange={v => setForm(f => ({ ...f, purchaseCost: v }))} />
            <div>
              <label style={S.label}>Asset category</label>
              <AppSelect value={form.assetCategory} onChange={value => setForm(f => ({ ...f, assetCategory: value }))} options={categories} />
            </div>
            <FormField label={`Useful life (years)${isPvtLtd ? " — Companies Act" : ""}`} type="number" value={form.usefulLifeYears} onChange={v => setForm(f => ({ ...f, usefulLifeYears: v }))} placeholder="e.g. 15" />
            <FormField label="Income Tax WDV rate (%)" type="number" value={form.incomeTaxRate} onChange={v => setForm(f => ({ ...f, incomeTaxRate: v }))} placeholder="e.g. 15" />
            <FormField label="IT Act block" value={form.incomeTaxBlock} onChange={v => setForm(f => ({ ...f, incomeTaxBlock: v }))} placeholder="e.g. Plant & Machinery 15%" />
          </div>
          {isPvtLtd && form.assetCategory && (
            <div style={{ background: "#EBF5FB", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#1B3F6E", marginBottom: 12 }}>
              Companies Act SLM rate: {((caRates[form.assetCategory] || 0.1) * 100).toFixed(2)}% per year
            </div>
          )}
          {error && <div style={S.errorBox}>{error}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={cancelForm} style={S.outlineBtn}>Cancel</button>
            <button onClick={saveAsset} disabled={saving} style={{ ...S.btn, flex: 1, background: saving ? "#90A4AE" : "#1A7A4A" }}>
              {saving ? "Saving…" : editing ? "Update asset" : "Add to register"}
            </button>
          </div>
        </div>
      )}

      {loading ? <div style={{ textAlign: "center", padding: 40, color: "#90A4AE" }}>Loading…</div>
      : assets.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, padding: 36, textAlign: "center", color: "#90A4AE" }}>
          No fixed assets recorded yet. Capital items approved from expense entries will appear here.
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.07)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Asset", "Category", "Purchase date", "Cost (₹)", isPvtLtd ? "Book value (₹)" : null, "IT WDV (₹)", "Status", isAdmin ? "" : null].filter(Boolean).map(h => (
                <th key={h} style={{ padding: "10px 14px", background: "#1B3F6E", color: "#fff", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", textAlign: h.includes("(₹)") ? "right" : "left" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {assets.map((a, i) => (
                <tr key={a._id} style={{ background: i % 2 === 0 ? "#fafbfc" : "#fff", borderBottom: "1px solid #F0F4F8" }}>
                  <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500 }}>{a.assetName}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: "#607D8B" }}>{categories.find(c => c.value === a.assetCategory)?.label || a.assetCategory}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, color: "#607D8B" }}>{a.purchaseDate ? new Date(a.purchaseDate).toLocaleDateString("en-IN") : "—"}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: "monospace", fontSize: 13 }}>{a.purchaseCost?.toLocaleString("en-IN")}</td>
                  {isPvtLtd && <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: "monospace", fontSize: 13 }}>{a.companiesActCurrentValue?.toLocaleString("en-IN") || "—"}</td>}
                  <td style={{ padding: "10px 14px", textAlign: "right", fontFamily: "monospace", fontSize: 13 }}>{a.incomeTaxWDV?.toLocaleString("en-IN") || "—"}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: a.status === "active" ? "#E8F5EE" : "#F5F5F5", color: a.status === "active" ? "#1A7A4A" : "#607D8B" }}>{a.status}</span>
                  </td>
                  {isAdmin && (
                    <td style={{ padding: "10px 14px" }}>
                      <button onClick={() => openEdit(a)} style={{ background: "none", border: "1px solid #CFD8DC", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", color: "#1B3F6E" }}>Edit</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// USER MANAGEMENT — Fixed: admin cannot be disabled, edit user added
// ─────────────────────────────────────────────────────────────────────────────
export function UserManagement({ entities }) {
  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form,    setForm]    = useState({ name: "", email: "", password: "", role: "hospital_staff", entityAccess: [] });
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    api.get("/users").then(res => setUsers(res.users || [])).catch(console.error).finally(() => setLoading(false));
  }, []);

  const roles = [
    { value: "hospital_staff",   label: "Role 1 — Hospital staff",   desc: "Upload billing and expenses only" },
    { value: "accountant_staff", label: "Role 2 — Accountant staff", desc: "Review, approve, export" },
    { value: "iicipl_admin",     label: "Role 3 — IICIPL Admin",     desc: "Full access — all entities" },
  ];

  function toggleEntity(id) {
    setForm(f => ({ ...f, entityAccess: f.entityAccess.includes(id) ? f.entityAccess.filter(e => e !== id) : [...f.entityAccess, id] }));
  }

  function openEdit(u) {
    setEditing(u._id);
    setForm({ name: u.name, email: u.email, password: "", role: u.role, entityAccess: u.entityAccess || [] });
    setShowAdd(true);
  }

  function cancelForm() {
    setShowAdd(false); setEditing(null);
    setForm({ name: "", email: "", password: "", role: "hospital_staff", entityAccess: [] });
    setError("");
  }

  async function saveUser() {
    if (!form.name || !form.email) { setError("Name and email are required."); return; }
    if (!editing && !form.password) { setError("Password is required for new users."); return; }
    if (form.role !== "iicipl_admin" && form.entityAccess.length === 0) { setError("Assign at least one entity for this role."); return; }
    setSaving(true); setError("");
    try {
      if (editing) {
        const payload = { name: form.name, email: form.email, role: form.role, entityAccess: form.entityAccess };
        if (form.password) payload.password = form.password;
        const res = await api.patch(`/users/${editing}`, payload);
        setUsers(u => u.map(x => x._id === editing ? res.user : x));
      } else {
        const res = await api.post("/users", form);
        setUsers(u => [res.user, ...u]);
      }
      cancelForm();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function toggleActive(userId, isActive, userRole) {
    // BUG FIX: Admin users cannot be disabled
    if (userRole === "iicipl_admin") return;
    try {
      await api.patch(`/users/${userId}`, { isActive: !isActive });
      setUsers(u => u.map(usr => usr._id === userId ? { ...usr, isActive: !isActive } : usr));
    } catch (err) { console.error(err); }
  }

  return (
    <div style={S.page}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1 style={S.h1}>User management</h1>
        {!showAdd && <button onClick={() => setShowAdd(true)} style={{ ...S.btn, width: "auto", padding: "10px 20px" }}>+ Add user</button>}
      </div>

      {showAdd && (
        <div style={S.card}>
          <div style={{ fontWeight: 600, color: "#1B3F6E", marginBottom: 16 }}>{editing ? "Edit user" : "Create new user"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
            <FormField label="Full name *" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} />
            <FormField label="Email address *" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} />
            <FormField label={editing ? "New password (leave blank to keep current)" : "Password *"} type="password" value={form.password} onChange={v => setForm(f => ({ ...f, password: v }))} placeholder="Min 8 characters" />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={S.label}>Role</label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {roles.map(r => (
                <label key={r.value} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", padding: "10px 12px", border: `1.5px solid ${form.role === r.value ? "#1B3F6E" : "#CFD8DC"}`, borderRadius: 8, background: form.role === r.value ? "#EBF5FB" : "#fff" }}>
                  <input type="radio" value={r.value} checked={form.role === r.value} onChange={() => setForm(f => ({ ...f, role: r.value, entityAccess: [] }))} style={{ marginTop: 2 }} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1B3F6E" }}>{r.label}</div>
                    <div style={{ fontSize: 12, color: "#90A4AE" }}>{r.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          {form.role !== "iicipl_admin" && (
            <div style={{ marginBottom: 16 }}>
              <label style={S.label}>Entity access *</label>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {entities.map(e => (
                  <label key={e._id} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", padding: "10px 12px", border: `1.5px solid ${form.entityAccess.includes(e._id) ? "#1B3F6E" : "#CFD8DC"}`, borderRadius: 8, background: form.entityAccess.includes(e._id) ? "#EBF5FB" : "#fff" }}>
                    <input type="checkbox" checked={form.entityAccess.includes(e._id)} onChange={() => toggleEntity(e._id)} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1B3F6E" }}>{e.name}</div>
                      <div style={{ fontSize: 11, color: "#90A4AE" }}>{e.location}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
          {error && <div style={S.errorBox}>{error}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={cancelForm} style={S.outlineBtn}>Cancel</button>
            <button onClick={saveUser} disabled={saving} style={{ ...S.btn, flex: 1, background: saving ? "#90A4AE" : "#1B3F6E" }}>
              {saving ? "Saving…" : editing ? "Update user" : "Create user"}
            </button>
          </div>
        </div>
      )}

      {loading ? <div style={{ textAlign: "center", padding: 40, color: "#90A4AE" }}>Loading users…</div> : (
        <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.07)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Name","Email","Role","Entity access","Last login","Status",""].map(h => (
                <th key={h} style={{ padding: "10px 14px", background: "#1B3F6E", color: "#fff", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em", textAlign: "left" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {users.map((u, i) => {
                const roleMeta = roles.find(r => r.value === u.role);
                const isAdmin  = u.role === "iicipl_admin";
                return (
                  <tr key={u._id} style={{ background: i % 2 === 0 ? "#fafbfc" : "#fff", borderBottom: "1px solid #F0F4F8" }}>
                    <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500 }}>{u.name}</td>
                    <td style={{ padding: "10px 14px", fontSize: 12, color: "#607D8B" }}>{u.email}</td>
                    <td style={{ padding: "10px 14px", fontSize: 12 }}>{roleMeta?.label || u.role}</td>
                    <td style={{ padding: "10px 14px", fontSize: 12, color: "#607D8B" }}>{isAdmin ? "All entities" : `${u.entityAccess?.length || 0} entity/ies`}</td>
                    <td style={{ padding: "10px 14px", fontSize: 12, color: "#90A4AE" }}>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString("en-IN") : "Never"}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: u.isActive ? "#E8F5EE" : "#F5F5F5", color: u.isActive ? "#1A7A4A" : "#90A4AE" }}>
                        {u.isActive ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td style={{ padding: "10px 14px", display: "flex", gap: 6 }}>
                      <button onClick={() => openEdit(u)} style={{ background: "none", border: "1px solid #CFD8DC", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", color: "#1B3F6E" }}>Edit</button>
                      {/* BUG FIX: Admin users cannot be disabled */}
                      {!isAdmin && (
                        <button onClick={() => toggleActive(u._id, u.isActive, u.role)} style={{ background: "none", border: "1px solid #CFD8DC", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", color: "#607D8B" }}>
                          {u.isActive ? "Disable" : "Enable"}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MANUAL JOURNAL ENTRY — New screen
// ─────────────────────────────────────────────────────────────────────────────
export function ManualJournalEntry({ entity }) {
  const [date,     setDate]     = useState(today());
  const [narration,setNarration]= useState("");
  const [lines,    setLines]    = useState([
    { accountName: "", debitOrCredit: "debit",  amount: "", description: "" },
    { accountName: "", debitOrCredit: "credit", amount: "", description: "" },
  ]);
  const [zohoAccounts, setZohoAccounts] = useState([]);
  const [submitting,   setSubmitting]   = useState(false);
  const [result,       setResult]       = useState(null);
  const [error,        setError]        = useState("");

  useEffect(() => {
    if (entity?.accountingSoftware?.type === "zoho_api" && entity?._id) {
      api.get(`/entities/${entity._id}/zoho/accounts`).then(res => setZohoAccounts(res.accounts || [])).catch(console.error);
    }
  }, [entity?._id]);

  const totalDebit  = lines.filter(l => l.debitOrCredit === "debit").reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const totalCredit = lines.filter(l => l.debitOrCredit === "credit").reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const isBalanced  = Math.abs(totalDebit - totalCredit) < 0.02;

  function addLine(type) {
    setLines(prev => [...prev, { accountName: "", debitOrCredit: type, amount: "", description: "" }]);
  }

  function updateLine(idx, field, value) {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  }

  function removeLine(idx) {
    if (lines.length <= 2) return;
    setLines(prev => prev.filter((_, i) => i !== idx));
  }

  async function submit() {
    if (!narration.trim()) { setError("Enter a narration / description for this entry."); return; }
    if (!isBalanced) { setError(`Entry is not balanced. Debit: ₹${totalDebit.toFixed(2)}, Credit: ₹${totalCredit.toFixed(2)}. Difference: ₹${Math.abs(totalDebit - totalCredit).toFixed(2)}`); return; }
    const emptyLines = lines.filter(l => !l.accountName || !l.amount);
    if (emptyLines.length > 0) { setError("All lines must have an account name and amount."); return; }

    setSubmitting(true); setError("");
    try {
      const res = await api.post(`/entities/${entity._id}/journal-entries/manual`, {
        date, narration, lines: lines.map(l => ({ ...l, amount: parseFloat(l.amount) })),
      });
      setResult(res);
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  const accountOptions = zohoAccounts.map(a => ({ value: a.account_name, label: `${a.account_name} (${a.account_type})` }));

  if (result) {
    return (
      <div style={S.page}>
        <div style={{ background: "#E8F5EE", borderRadius: 14, padding: 36, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
          <h2 style={{ color: "#1A7A4A", margin: "0 0 8px" }}>Journal entry created</h2>
          <p style={{ color: "#607D8B", margin: "0 0 20px" }}>Entry is in Draft status. Go to Journal Entries to approve and push to Zoho.</p>
          <button onClick={() => { setResult(null); setLines([{ accountName: "", debitOrCredit: "debit", amount: "", description: "" }, { accountName: "", debitOrCredit: "credit", amount: "", description: "" }]); setNarration(""); }} style={S.btn}>Create another entry</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Manual journal entry</h1>
      <p style={S.subtitle}>For transfer entries, adjustments, corrections, and any entry not covered by other screens.</p>

      <div style={S.card}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14, marginBottom: 16 }}>
          <div>
            <label style={S.label}>Date *</label>
            <AppDatePicker value={date} onChange={setDate} maxDate={new Date()} />
          </div>
          <div>
            <label style={S.label}>Narration *</label>
            <input type="text" value={narration} onChange={e => setNarration(e.target.value)} placeholder="e.g. Transfer to savings account, GST payment June 2026" style={{ ...S.input, width: "100%" }} />
          </div>
        </div>

        {/* Entry lines */}
        <div style={{ background: "#1B3F6E", display: "grid", gridTemplateColumns: "3fr 1fr 2fr 1fr 28px", gap: 8, padding: "8px 12px", borderRadius: "8px 8px 0 0" }}>
          {["Account", "Dr / Cr", "Amount (₹)", "Description", ""].map(h => (
            <div key={h} style={{ fontSize: 11, fontWeight: 600, color: "#fff", textTransform: "uppercase", letterSpacing: ".04em" }}>{h}</div>
          ))}
        </div>

        {lines.map((line, idx) => (
          <div key={idx} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 2fr 1fr 28px", gap: 8, padding: "6px 12px", background: idx % 2 === 0 ? "#fafbfc" : "#fff", borderBottom: "1px solid #F0F4F8", alignItems: "center" }}>
            <div>
              {accountOptions.length > 0 ? (
                <AppSelect value={line.accountName} onChange={v => updateLine(idx, "accountName", v)} options={accountOptions} placeholder="Select account from Zoho" />
              ) : (
                <input type="text" value={line.accountName} onChange={e => updateLine(idx, "accountName", e.target.value)} placeholder="Account name" style={S.gridInput} />
              )}
            </div>
            <div>
              <select value={line.debitOrCredit} onChange={e => updateLine(idx, "debitOrCredit", e.target.value)} style={{ ...S.gridInput, color: line.debitOrCredit === "debit" ? "#1A7A4A" : "#B71C1C", fontWeight: 600 }}>
                <option value="debit">Dr</option>
                <option value="credit">Cr</option>
              </select>
            </div>
            <div>
              <input type="number" min="0" step="0.01" value={line.amount} onChange={e => updateLine(idx, "amount", e.target.value)} placeholder="0.00" style={{ ...S.gridInput, textAlign: "right" }} />
            </div>
            <div>
              <input type="text" value={line.description} onChange={e => updateLine(idx, "description", e.target.value)} placeholder="Optional" style={S.gridInput} />
            </div>
            <button onClick={() => removeLine(idx)} style={{ background: "none", border: "none", color: "#B0BEC5", fontSize: 18, cursor: lines.length <= 2 ? "not-allowed" : "pointer", padding: 0 }}>×</button>
          </div>
        ))}

        {/* Totals */}
        <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 2fr 1fr 28px", gap: 8, padding: "10px 12px", background: "#EBF5FB", borderTop: "2px solid #B0C4D8" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1B3F6E" }}>Total</div>
          <div></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: "#1A7A4A" }}>Dr: ₹{totalDebit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
            <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: "#B71C1C" }}>Cr: ₹{totalCredit.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
          </div>
          <div></div>
          <div></div>
        </div>

        {/* Balance indicator */}
        <div style={{ padding: "10px 12px" }}>
          <div style={{ padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: isBalanced ? "#E8F5EE" : "#FFEBEE", color: isBalanced ? "#1A7A4A" : "#B71C1C" }}>
            {isBalanced ? "✓ Entry is balanced" : `✗ Not balanced — difference of ₹${Math.abs(totalDebit - totalCredit).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
          </div>
        </div>

        {/* Add line buttons */}
        <div style={{ display: "flex", gap: 10, padding: "0 12px 12px" }}>
          <button onClick={() => addLine("debit")}  style={{ ...S.outlineBtn, fontSize: 12, padding: "7px 14px" }}>+ Add debit line</button>
          <button onClick={() => addLine("credit")} style={{ ...S.outlineBtn, fontSize: 12, padding: "7px 14px" }}>+ Add credit line</button>
        </div>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      <button onClick={submit} disabled={submitting || !isBalanced} style={{ ...S.btn, background: submitting || !isBalanced ? "#90A4AE" : "#1B3F6E", cursor: submitting || !isBalanced ? "not-allowed" : "pointer" }}>
        {submitting ? "Creating entry…" : "Create journal entry"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STATUTORY PAYMENTS — New screen
// GST payment, EPF/ESI deposit, TDS deposit
// ─────────────────────────────────────────────────────────────────────────────
export function StatutoryPayments({ entity }) {
  const [tab,      setTab]      = useState("gst"); // gst | epf | tds
  const [form,     setForm]     = useState({ date: today(), amount: "", bankAccount: "Bank Account — HDFC", reference: "", period: currentPeriod() });
  const [submitting,setSubmitting] = useState(false);
  const [result,   setResult]   = useState(null);
  const [error,    setError]    = useState("");
  const [zohoAccounts, setZohoAccounts] = useState([]);

  useEffect(() => {
    if (entity?.accountingSoftware?.type === "zoho_api" && entity?._id) {
      api.get(`/entities/${entity._id}/zoho/accounts`).then(res => setZohoAccounts(res.accounts || [])).catch(console.error);
    }
  }, [entity?._id]);

  const bankOptions = zohoAccounts.filter(a => a.account_type?.toLowerCase().includes("bank")).map(a => ({ value: a.account_name, label: a.account_name }));
  if (!bankOptions.length) {
    bankOptions.push({ value: "Bank Account — HDFC", label: "Bank Account — HDFC" }, { value: "Bank Account — KVB", label: "Bank Account — KVB" });
  }

  const tabs = [
    { key: "gst",  label: "GST Payment",     debitAccount: "CGST Payable / SGST Payable",  hint: "Enter total GST being paid. System will debit CGST Payable and SGST Payable equally." },
    { key: "epf",  label: "EPF / ESI Deposit", debitAccount: "EPF Payable / ESI Payable",  hint: "Enter total amount deposited. System debits EPF Payable and ESI Payable." },
    { key: "tds",  label: "TDS Deposit",      debitAccount: "TDS Payable 194J / 194C",     hint: "Enter total TDS deposited. Specify section for correct ledger debit." },
  ];

  const [tdsSection, setTdsSection] = useState("194J");

  async function submit() {
    if (!form.amount || parseFloat(form.amount) <= 0) { setError("Enter a valid amount."); return; }
    if (!form.date) { setError("Select a payment date."); return; }
    setSubmitting(true); setError("");
    try {
      const res = await api.post(`/entities/${entity._id}/statutory-payment`, {
        type: tab, date: form.date, amount: parseFloat(form.amount),
        bankAccount: form.bankAccount, reference: form.reference, period: form.period, tdsSection,
      });
      setResult(res);
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  if (result) {
    return (
      <div style={S.page}>
        <div style={{ background: "#E8F5EE", borderRadius: 14, padding: 36, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
          <h2 style={{ color: "#1A7A4A", margin: "0 0 8px" }}>Payment entry created</h2>
          <p style={{ color: "#607D8B", margin: "0 0 20px" }}>Entry is in Draft. Go to Journal Entries to approve and push to Zoho. Obligation card will clear once approved.</p>
          <button onClick={() => { setResult(null); setForm({ date: today(), amount: "", bankAccount: "Bank Account — HDFC", reference: "", period: currentPeriod() }); }} style={S.btn}>Record another payment</button>
        </div>
      </div>
    );
  }

  const activeTab = tabs.find(t => t.key === tab);

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Statutory payments</h1>
      <p style={S.subtitle}>Record GST, EPF/ESI, and TDS deposits to government. Clears the obligation from the dashboard.</p>

      {/* Tab selector */}
      <div style={{ display: "flex", gap: 0, background: "#E3F2FD", borderRadius: 8, padding: 4, marginBottom: 20, width: "fit-content" }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: "8px 20px", border: "none", borderRadius: 6, fontSize: 13, cursor: "pointer", background: tab === t.key ? "#1B3F6E" : "transparent", color: tab === t.key ? "#fff" : "#1B3F6E", fontWeight: tab === t.key ? 600 : 400 }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ ...S.card, background: "#EBF5FB", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#1B3F6E" }}><strong>What this creates:</strong> {activeTab?.hint}</div>
      </div>

      <div style={S.card}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div>
            <label style={S.label}>Payment date *</label>
            <AppDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} maxDate={new Date()} />
          </div>
          <div>
            <label style={S.label}>Period (month being paid for)</label>
            <input type="month" value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} style={{ ...S.input, width: "100%" }} />
          </div>
          <div>
            <label style={S.label}>Amount paid (₹) *</label>
            <input type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" style={{ ...S.input, width: "100%", textAlign: "right" }} />
          </div>
          <div>
            <label style={S.label}>Bank account *</label>
            <AppSelect value={form.bankAccount} onChange={v => setForm(f => ({ ...f, bankAccount: v }))} options={bankOptions} />
          </div>
          {tab === "tds" && (
            <div>
              <label style={S.label}>TDS Section</label>
              <AppSelect value={tdsSection} onChange={setTdsSection} options={[{ value: "194J", label: "194J — Professional fees" }, { value: "194C", label: "194C — Contractor" }, { value: "192B", label: "192B — Salary" }]} />
            </div>
          )}
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={S.label}>Challan / UTR / Reference number</label>
            <input type="text" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="e.g. OLTAS Challan No. 12345 or UTR number" style={{ ...S.input, width: "100%" }} />
          </div>
        </div>

        {error && <div style={{ ...S.errorBox, marginTop: 14 }}>{error}</div>}

        <button onClick={submit} disabled={submitting} style={{ ...S.btn, marginTop: 16, background: submitting ? "#90A4AE" : "#1B3F6E" }}>
          {submitting ? "Creating entry…" : `Record ${activeTab?.label}`}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SALARY JOURNAL — New screen
// ─────────────────────────────────────────────────────────────────────────────
export function SalaryJournal({ entity }) {
  const [form, setForm] = useState({
    date: today(), period: currentPeriod(),
    grossSalary: "", employeeEPF: "", employerEPF: "",
    employeeESI: "", employerESI: "", professionalTax: "",
    tdsSalary: "", netSalary: "", bankAccount: "Bank Account — HDFC",
    narration: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result,     setResult]     = useState(null);
  const [error,      setError]      = useState("");
  const [zohoAccounts, setZohoAccounts] = useState([]);

  useEffect(() => {
    if (entity?.accountingSoftware?.type === "zoho_api" && entity?._id) {
      api.get(`/entities/${entity._id}/zoho/accounts`).then(res => setZohoAccounts(res.accounts || [])).catch(console.error);
    }
  }, [entity?._id]);

  const bankOptions = zohoAccounts.filter(a => a.account_type?.toLowerCase().includes("bank")).map(a => ({ value: a.account_name, label: a.account_name }));
  if (!bankOptions.length) bankOptions.push({ value: "Bank Account — HDFC", label: "Bank Account — HDFC" }, { value: "Bank Account — KVB", label: "Bank Account — KVB" });

  function n(v) { return parseFloat(v || 0) || 0; }

  const computedNet = n(form.grossSalary) - n(form.employeeEPF) - n(form.employeeESI) - n(form.professionalTax) - n(form.tdsSalary);
  const totalDebit  = n(form.grossSalary) + n(form.employerEPF) + n(form.employerESI);
  const totalCredit = n(form.employeeEPF) + n(form.employerEPF) + n(form.employeeESI) + n(form.employerESI) + n(form.professionalTax) + n(form.tdsSalary) + computedNet;
  const isBalanced  = Math.abs(totalDebit - totalCredit) < 1;

  async function submit() {
    if (!form.grossSalary) { setError("Enter gross salary amount."); return; }
    setSubmitting(true); setError("");
    try {
      const res = await api.post(`/entities/${entity._id}/salary-journal`, { ...form, netSalary: computedNet, bankAccount: form.bankAccount });
      setResult(res);
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  if (result) {
    return (
      <div style={S.page}>
        <div style={{ background: "#E8F5EE", borderRadius: 14, padding: 36, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
          <h2 style={{ color: "#1A7A4A", margin: "0 0 8px" }}>Salary journal entry created</h2>
          <p style={{ color: "#607D8B", margin: "0 0 20px" }}>Go to Journal Entries to approve and push to Zoho.</p>
          <button onClick={() => setResult(null)} style={S.btn}>Create another</button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <h1 style={S.h1}>Salary journal</h1>
      <p style={S.subtitle}>Monthly salary entry with EPF, ESI, Professional Tax, and TDS auto-split.</p>

      <div style={S.card}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
          <div>
            <label style={S.label}>Payment date *</label>
            <AppDatePicker value={form.date} onChange={v => setForm(f => ({ ...f, date: v }))} maxDate={new Date()} />
          </div>
          <div>
            <label style={S.label}>Salary month</label>
            <input type="month" value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} style={{ ...S.input, width: "100%" }} />
          </div>
          <div>
            <label style={S.label}>Bank account</label>
            <AppSelect value={form.bankAccount} onChange={v => setForm(f => ({ ...f, bankAccount: v }))} options={bankOptions} />
          </div>
        </div>

        <div style={{ ...S.sectionLabel, marginBottom: 10 }}>Salary amounts</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 16 }}>
          <SalaryField label="Gross salary (₹) *"       value={form.grossSalary}    onChange={v => setForm(f => ({ ...f, grossSalary: v }))}    hint="Total CTC before deductions" />
          <SalaryField label="Employee EPF (₹)"          value={form.employeeEPF}    onChange={v => setForm(f => ({ ...f, employeeEPF: v }))}    hint="12% of basic — deducted from employee" />
          <SalaryField label="Employer EPF (₹)"          value={form.employerEPF}    onChange={v => setForm(f => ({ ...f, employerEPF: v }))}    hint="12% of basic — hospital pays" />
          <SalaryField label="Employee ESI (₹)"          value={form.employeeESI}    onChange={v => setForm(f => ({ ...f, employeeESI: v }))}    hint="0.75% of gross — deducted" />
          <SalaryField label="Employer ESI (₹)"          value={form.employerESI}    onChange={v => setForm(f => ({ ...f, employerESI: v }))}    hint="3.25% of gross — hospital pays" />
          <SalaryField label="Professional Tax (₹)"      value={form.professionalTax} onChange={v => setForm(f => ({ ...f, professionalTax: v }))} hint="₹200/month per employee (Tamil Nadu)" />
          <SalaryField label="TDS on salary 192B (₹)"   value={form.tdsSalary}      onChange={v => setForm(f => ({ ...f, tdsSalary: v }))}      hint="If applicable — deducted from employee" />
        </div>

        <div style={S.sectionLabel}>Narration</div>
        <input type="text" value={form.narration} onChange={e => setForm(f => ({ ...f, narration: e.target.value }))} placeholder={`Salary for ${form.period}`} style={{ ...S.input, width: "100%", marginBottom: 16 }} />

        {/* Preview */}
        {n(form.grossSalary) > 0 && (
          <div style={{ background: "#f8fafc", borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#455A64", marginBottom: 10, textTransform: "uppercase", letterSpacing: ".06em" }}>Journal entry preview</div>
            {[
              { account: "Salaries & Wages Expense",     side: "Dr", amt: n(form.grossSalary),     color: "#1A7A4A" },
              { account: "Employer EPF Contribution",    side: "Dr", amt: n(form.employerEPF),     color: "#1A7A4A" },
              { account: "Employer ESI Contribution",    side: "Dr", amt: n(form.employerESI),     color: "#1A7A4A" },
              { account: "Employee EPF Payable",         side: "Cr", amt: n(form.employeeEPF),     color: "#B71C1C" },
              { account: "Employer EPF Payable",         side: "Cr", amt: n(form.employerEPF),     color: "#B71C1C" },
              { account: "Employee ESI Payable",         side: "Cr", amt: n(form.employeeESI),     color: "#B71C1C" },
              { account: "Employer ESI Payable",         side: "Cr", amt: n(form.employerESI),     color: "#B71C1C" },
              { account: "Professional Tax Payable",     side: "Cr", amt: n(form.professionalTax), color: "#B71C1C" },
              { account: "TDS Payable — 192B",           side: "Cr", amt: n(form.tdsSalary),       color: "#B71C1C" },
              { account: "Net Salary Payable / Bank",    side: "Cr", amt: computedNet,             color: "#B71C1C" },
            ].filter(l => l.amt > 0).map((l, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #F0F4F8", fontSize: 12 }}>
                <span style={{ color: "#455A64" }}>{l.account}</span>
                <span style={{ color: l.color, fontWeight: 600, fontFamily: "monospace" }}>{l.side}  ₹{l.amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0 0", fontSize: 13, fontWeight: 700 }}>
              <span style={{ color: "#1B3F6E" }}>Balance check</span>
              <span style={{ color: isBalanced ? "#1A7A4A" : "#B71C1C" }}>{isBalanced ? "✓ Balanced" : "✗ Not balanced"}</span>
            </div>
          </div>
        )}

        {error && <div style={S.errorBox}>{error}</div>}

        <button onClick={submit} disabled={submitting || !n(form.grossSalary)} style={{ ...S.btn, background: submitting ? "#90A4AE" : "#1B3F6E" }}>
          {submitting ? "Creating entry…" : "Create salary journal entry"}
        </button>
      </div>
    </div>
  );
}

function SalaryField({ label, value, onChange, hint }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      <input type="number" min="0" step="1" value={value} onChange={e => onChange(e.target.value)} placeholder="0" style={{ ...S.input, width: "100%", textAlign: "right" }} />
      {hint && <div style={{ fontSize: 11, color: "#90A4AE", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TPA SETTLEMENT — New screen
// ─────────────────────────────────────────────────────────────────────────────
export function TPASettlement({ entity }) {
  const [debtors,  setDebtors]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [selected, setSelected] = useState(null);
  const [form,     setForm]     = useState({ amountReceived: "", tdsDeducted: "", settlementDate: today(), referenceNo: "", bankAccount: "Bank Account — HDFC" });
  const [submitting,setSubmitting] = useState(false);
  const [error,    setError]    = useState("");
  const [zohoAccounts, setZohoAccounts] = useState([]);

  useEffect(() => {
    if (!entity?._id) return;
    api.get(`/entities/${entity._id}/tpa-debtors?status=pending,partial`).then(res => setDebtors(res.debtors || [])).catch(console.error).finally(() => setLoading(false));
    if (entity?.accountingSoftware?.type === "zoho_api") {
      api.get(`/entities/${entity._id}/zoho/accounts`).then(res => setZohoAccounts(res.accounts || [])).catch(console.error);
    }
  }, [entity?._id]);

  const bankOptions = zohoAccounts.filter(a => a.account_type?.toLowerCase().includes("bank")).map(a => ({ value: a.account_name, label: a.account_name }));
  if (!bankOptions.length) bankOptions.push({ value: "Bank Account — HDFC", label: "Bank Account — HDFC" }, { value: "Bank Account — KVB", label: "Bank Account — KVB" });

  function selectDebtor(d) {
    setSelected(d);
    const tds = Math.round(d.balance * 0.10 * 100) / 100;
    setForm({ amountReceived: (d.balance - tds).toFixed(2), tdsDeducted: tds.toFixed(2), settlementDate: today(), referenceNo: "", bankAccount: "Bank Account — HDFC" });
  }

  async function settle() {
    if (!selected) { setError("Select a TPA record first."); return; }
    if (!form.amountReceived) { setError("Enter amount received."); return; }
    setSubmitting(true); setError("");
    try {
      await api.patch(`/entities/${entity._id}/tpa-debtors/${selected._id}/settle`, {
        amountReceived: parseFloat(form.amountReceived),
        tdsDeducted:    parseFloat(form.tdsDeducted || 0),
        referenceNo:    form.referenceNo,
        settlementDate: form.settlementDate,
        bankAccount:    form.bankAccount,
      });
      setDebtors(prev => prev.map(d => d._id === selected._id ? { ...d, amountReceived: d.amountReceived + parseFloat(form.amountReceived), balance: d.balance - parseFloat(form.amountReceived) - parseFloat(form.tdsDeducted || 0), status: d.balance - parseFloat(form.amountReceived) - parseFloat(form.tdsDeducted || 0) <= 0 ? "settled" : "partial" } : d));
      setSelected(null);
      setForm({ amountReceived: "", tdsDeducted: "", settlementDate: today(), referenceNo: "", bankAccount: "Bank Account — HDFC" });
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  }

  if (!entity) return <div style={S.page}><div style={S.emptyBox}>No entity available.</div></div>;

  return (
    <div style={S.page}>
      <h1 style={S.h1}>TPA / Insurance settlement</h1>
      <p style={S.subtitle}>Record payments received from insurance companies. TDS deduction auto-calculated at 10% (Section 194J).</p>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Left — pending list */}
        <div>
          <div style={{ fontWeight: 600, color: "#1B3F6E", marginBottom: 10, fontSize: 14 }}>Pending settlements</div>
          {loading ? <div style={{ color: "#90A4AE", fontSize: 13 }}>Loading…</div>
          : debtors.filter(d => d.status !== "settled").length === 0 ? <div style={{ color: "#90A4AE", fontSize: 13 }}>No pending settlements</div>
          : debtors.filter(d => d.status !== "settled").map(d => (
            <div key={d._id} onClick={() => selectDebtor(d)} style={{ background: selected?._id === d._id ? "#EBF5FB" : "#fff", border: `1.5px solid ${selected?._id === d._id ? "#1B3F6E" : "#E0E7EF"}`, borderRadius: 10, padding: "12px 14px", marginBottom: 8, cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#1B3F6E" }}>{d.patientName}</div>
                  <div style={{ fontSize: 12, color: "#607D8B" }}>{d.tpaName}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#C85A00" }}>₹{d.balance?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>
                  <div style={{ fontSize: 10, color: "#90A4AE" }}>Balance due</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                <span style={{ fontSize: 11, background: d.status === "partial" ? "#FFF3E0" : "#FFEBEE", color: d.status === "partial" ? "#C85A00" : "#B71C1C", padding: "2px 8px", borderRadius: 10 }}>{d.status}</span>
                {d.dischargeDate && <span style={{ fontSize: 11, color: "#90A4AE" }}>Discharge: {new Date(d.dischargeDate).toLocaleDateString("en-IN")}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* Right — settlement form */}
        <div>
          {!selected ? (
            <div style={{ background: "#f8fafc", borderRadius: 12, padding: 24, textAlign: "center", color: "#90A4AE", fontSize: 13 }}>
              Select a TPA record from the left to record payment
            </div>
          ) : (
            <div style={S.card}>
              <div style={{ fontWeight: 600, color: "#1B3F6E", marginBottom: 4, fontSize: 14 }}>Settle: {selected.patientName}</div>
              <div style={{ fontSize: 12, color: "#607D8B", marginBottom: 16 }}>{selected.tpaName} · Balance: ₹{selected.balance?.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={S.label}>Amount received (₹) *</label>
                  <input type="number" min="0" step="0.01" value={form.amountReceived} onChange={e => setForm(f => ({ ...f, amountReceived: e.target.value }))} style={{ ...S.input, width: "100%", textAlign: "right" }} />
                </div>
                <div>
                  <label style={S.label}>TDS deducted (₹) — 10% u/s 194J</label>
                  <input type="number" min="0" step="0.01" value={form.tdsDeducted} onChange={e => setForm(f => ({ ...f, tdsDeducted: e.target.value }))} style={{ ...S.input, width: "100%", textAlign: "right" }} />
                </div>
                <div>
                  <label style={S.label}>Settlement date *</label>
                  <AppDatePicker value={form.settlementDate} onChange={v => setForm(f => ({ ...f, settlementDate: v }))} maxDate={new Date()} />
                </div>
                <div>
                  <label style={S.label}>Bank account</label>
                  <AppSelect value={form.bankAccount} onChange={v => setForm(f => ({ ...f, bankAccount: v }))} options={bankOptions} />
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={S.label}>Reference / UTR</label>
                  <input type="text" value={form.referenceNo} onChange={e => setForm(f => ({ ...f, referenceNo: e.target.value }))} placeholder="Cheque number or NEFT/RTGS UTR" style={{ ...S.input, width: "100%" }} />
                </div>
              </div>

              {/* Journal preview */}
              {parseFloat(form.amountReceived) > 0 && (
                <div style={{ background: "#f0f4f8", borderRadius: 8, padding: 12, margin: "12px 0", fontSize: 12 }}>
                  <div style={{ fontWeight: 600, color: "#455A64", marginBottom: 8 }}>Journal entry that will be created:</div>
                  <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e0e7ef", paddingBottom: 4, marginBottom: 4 }}>
                    <span style={{ color: "#455A64" }}>{form.bankAccount}</span>
                    <span style={{ color: "#1A7A4A", fontFamily: "monospace" }}>Dr ₹{parseFloat(form.amountReceived || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                  {parseFloat(form.tdsDeducted) > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e0e7ef", paddingBottom: 4, marginBottom: 4 }}>
                      <span style={{ color: "#455A64" }}>TDS Receivable — 194J</span>
                      <span style={{ color: "#1A7A4A", fontFamily: "monospace" }}>Dr ₹{parseFloat(form.tdsDeducted || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ color: "#455A64" }}>TPA / Insurance Debtors — {selected.tpaName}</span>
                    <span style={{ color: "#B71C1C", fontFamily: "monospace" }}>Cr ₹{(parseFloat(form.amountReceived || 0) + parseFloat(form.tdsDeducted || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                </div>
              )}

              {error && <div style={S.errorBox}>{error}</div>}

              <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                <button onClick={() => setSelected(null)} style={S.outlineBtn}>Cancel</button>
                <button onClick={settle} disabled={submitting} style={{ ...S.btn, flex: 1, background: submitting ? "#90A4AE" : "#1A7A4A" }}>
                  {submitting ? "Recording…" : "Record settlement"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function FormField({ label, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <label style={S.label}>{label}</label>
      {type === "date" ? (
        <AppDatePicker value={value} onChange={onChange} placeholder={placeholder || "Select date"} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || ""} style={{ ...S.input, width: "100%", boxSizing: "border-box" }} />
      )}
    </div>
  );
}

function today() { return new Date().toISOString().split("T")[0]; }
function currentPeriod() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

// ── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  page:        { maxWidth: 900, margin: "0 auto" },
  h1:          { fontSize: 22, fontWeight: 700, color: "#1B3F6E", margin: "0 0 6px" },
  subtitle:    { fontSize: 14, color: "#607D8B", margin: "0 0 24px" },
  card:        { background: "#fff", borderRadius: 12, padding: 22, marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,.07)" },
  sectionLabel:{ fontSize: 11, fontWeight: 600, color: "#455A64", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 10, display: "block" },
  label:       { display: "block", fontSize: 11, fontWeight: 600, color: "#455A64", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 },
  input:       { padding: "9px 12px", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, fontFamily: "inherit" },
  gridInput:   { width: "100%", padding: "6px 8px", border: "1px solid #CFD8DC", borderRadius: 6, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" },
  select:      { width: "100%", padding: "9px 12px", border: "1.5px solid #CFD8DC", borderRadius: 8, fontSize: 13, fontFamily: "inherit", background: "#fff" },
  btn:         { width: "100%", padding: 13, background: "#1B3F6E", color: "#fff", border: "none", borderRadius: 9, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  outlineBtn:  { padding: "11px 20px", background: "#fff", border: "2px solid #1B3F6E", color: "#1B3F6E", borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: "pointer" },
  errorBox:    { background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, padding: "12px 16px", color: "#B71C1C", fontSize: 13, marginBottom: 12 },
  emptyBox:    { background: "#FFF3E0", border: "1px solid #FFE0B2", borderRadius: 10, padding: 20, color: "#7B4F00", fontSize: 13 },
};
